import type { components } from '../../api/generated/schema'
import { parseMoney } from '../../api/parseMoney'

/**
 * The only place a recurring response is read. Everything past this module sees
 * a `Bill`, `Entry`, `Period` or `Suggestion`: money already parsed to integer
 * cents, the free-text `frequency` and `status` strings already narrowed.
 *
 * **Fields deliberately not read:** `has_price_change`, `price_change_amount`,
 * `is_current_period_paid`, `missed_periods_count` and the calendar's
 * `amount_*` / `total_*` sums. They mislead on this surface (single-period flags,
 * sums that mix currencies and inflows), so the client derives what it shows
 * from entries instead. `test/recurringServerFieldsGuard.test.ts` enforces it.
 */

export type Frequency =
  | 'weekly'
  | 'bi_weekly'
  | 'semi_monthly'
  | 'monthly'
  | 'quarterly'
  | 'semi_annually'
  | 'yearly'
  | 'unknown'

/** The frequencies a user may choose, in the order the picker lists them (S4). */
export const PICKABLE_FREQUENCIES: readonly Exclude<Frequency, 'unknown'>[] = [
  'weekly',
  'bi_weekly',
  'semi_monthly',
  'monthly',
  'quarterly',
  'semi_annually',
  'yearly',
]

/** Every spelling the backend and its providers are known to send, lower-cased. */
const FREQUENCY_ALIASES: Record<string, Frequency> = {
  weekly: 'weekly',
  biweekly: 'bi_weekly',
  bi_weekly: 'bi_weekly',
  semi_monthly: 'semi_monthly',
  semimonthly: 'semi_monthly',
  monthly: 'monthly',
  quarterly: 'quarterly',
  semi_annually: 'semi_annually',
  semiannually: 'semi_annually',
  yearly: 'yearly',
  annually: 'yearly',
  annual: 'yearly',
}

/**
 * Case-insensitive, whitespace-trimmed, and an exact match: `monthlyx` is not
 * `monthly`. Anything unrecognised is `unknown`, which a form never offers.
 */
export function normaliseFrequency(raw: string): Frequency {
  return FREQUENCY_ALIASES[raw.trim().toLowerCase()] ?? 'unknown'
}

const FREQUENCY_LABELS: Record<Frequency, string> = {
  weekly: 'Weekly',
  bi_weekly: 'Every 2 weeks',
  semi_monthly: 'Twice a month',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Every 6 months',
  yearly: 'Yearly',
  unknown: 'Irregular',
}

export function frequencyLabel(f: Frequency): string {
  return FREQUENCY_LABELS[f]
}

export type AccountStatus = 'ok' | 'reauth_required' | 'disconnected' | 'none' | null

const ACCOUNT_STATUSES = ['ok', 'reauth_required', 'disconnected', 'none'] as const

export type Bill = {
  id: string
  /** `description ?? merchant_name`. */
  name: string
  merchant: string
  accountId: string | null
  categoryId: string | null
  currency: string
  /** `direction === 'inflow'`. */
  isInflow: boolean
  frequency: Frequency
  /** `parseMoney(expected_amount ?? average_amount)`. */
  expectedCents: number
  /**
   * `parseMoney(average_amount)` alone. A variable bill's amount IS its
   * average; its `expected_amount` is a separate figure. The edit form seeds
   * and writes a variable bill's amount from this, so a non-null
   * `expected_amount` can never make an edit compare against one field and
   * write another.
   */
  averageCents: number
  amountType: 'fixed' | 'variable' | null
  /** `predicted_next_date`, YYYY-MM-DD: a calendar date, never a Date. */
  nextDue: string | null
  firstDate: string | null
  /** `last_date` — the last payment date. */
  lastDate: string | null
  /**
   * `parseMoney(last_amount)`: what the most recent payment actually was, null
   * when the wire omits it. The Passbook's Paid row shows it (P5), falling back
   * to `expectedCents` only when this is null.
   */
  lastCents: number | null
  /** `is_paused || status === 'paused'`. */
  isPaused: boolean
  /** `status === 'active' && !isPaused && !is_removed`. */
  isActive: boolean
  isRemoved: boolean
  accountStatus: AccountStatus
  reminderDays: number | null
  tags: string[]
  icon: string | null
  iconUrl: string | null
  note: string | null
  version: number
}

export type PeriodStatus = 'paid' | 'missed' | 'upcoming' | 'skipped' | 'unknown' | 'paused'
export type Payment = {
  id: string
  /** Null for a cash payment: there is no bank transaction behind it. */
  transactionId: string | null
  /** `transaction_date`, YYYY-MM-DD. */
  paidOn: string
  /**
   * `parseMoney(transaction_amount)`, cash included: the backend fills
   * `transaction_amount` from `paid_amount` for a cash payment, so it is the
   * real amount paid. Null only when the wire field itself is null.
   */
  amountCents: number | null
  matchedBy: string | null
  amountFlagged: boolean
  /**
   * `merchant_name`: the settling transaction's merchant as the server names
   * it (the period sheet's "Settled by"). Empty for a cash payment.
   */
  merchant: string
}
export type Entry = {
  bill: Bill
  dueDate: string
  status: PeriodStatus
  payment: Payment | null
  expectedCents: number
}
export type Period = { dueDate: string; status: PeriodStatus; payment: Payment | null }
export type Suggestion = {
  id: string
  merchant: string
  frequency: Frequency
  averageCents: number
  accountId: string | null
  seenCount: number
}

type RecurringResponse = components['schemas']['RecurringResponse']

function accountStatusOf(s: string | null | undefined): AccountStatus {
  return ACCOUNT_STATUSES.find((v) => v === s) ?? null
}

function amountTypeOf(s: string): Bill['amountType'] {
  return s === 'fixed' || s === 'variable' ? s : null
}

export function toBill(r: RecurringResponse): Bill {
  const isPaused = r.is_paused || r.status === 'paused'
  return {
    id: r.id,
    name: r.description ?? r.merchant_name,
    merchant: r.merchant_name,
    accountId: r.account_id ?? null,
    categoryId: r.category_id ?? null,
    currency: r.iso_currency_code,
    isInflow: r.direction === 'inflow',
    frequency: normaliseFrequency(r.frequency),
    expectedCents: parseMoney(r.expected_amount ?? r.average_amount),
    averageCents: parseMoney(r.average_amount),
    amountType: amountTypeOf(r.amount_type),
    nextDue: r.predicted_next_date ?? null,
    firstDate: r.first_date ?? null,
    lastDate: r.last_date ?? null,
    lastCents: r.last_amount == null ? null : parseMoney(r.last_amount),
    isPaused,
    isActive: r.status === 'active' && !isPaused && !r.is_removed,
    isRemoved: r.is_removed,
    accountStatus: accountStatusOf(r.account_status),
    reminderDays: r.reminder_days ?? null,
    tags: r.tags,
    icon: r.icon ?? null,
    iconUrl: r.icon_url ?? null,
    note: r.note ?? null,
    version: r.version,
  }
}

function toPayment(p: components['schemas']['RecurringPaymentResponse'] | null | undefined): Payment | null {
  return p ? paymentOf(p) : null
}

function paymentOf(p: components['schemas']['RecurringPaymentResponse']): Payment {
  const transactionId = p.transaction_id ?? null
  return {
    id: p.id,
    transactionId,
    paidOn: p.transaction_date,
    amountCents: p.transaction_amount == null ? null : parseMoney(p.transaction_amount),
    matchedBy: p.matched_by || null,
    amountFlagged: p.amount_flagged,
    merchant: p.merchant_name,
  }
}

/**
 * `POST /recurring/{id}/payments`' answer: the payment, and the period the
 * server filed it under. That is `snap_to_grid` of the date sent, so a caller
 * compares it rather than assuming it is the one asked for.
 */
export function toFiledPayment(p: components['schemas']['RecurringPaymentResponse']): { dueDate: string | null; payment: Payment } {
  return { dueDate: p.due_date ?? null, payment: paymentOf(p) }
}

export function toEntry(e: components['schemas']['CalendarEntry']): Entry {
  return {
    bill: toBill(e.recurring),
    dueDate: e.due_date,
    status: e.detail_status,
    payment: toPayment(e.payment),
    expectedCents: parseMoney(e.expected_amount),
  }
}

export function toPeriod(p: components['schemas']['PeriodEntry']): Period {
  return { dueDate: p.due_date, status: p.detail_status, payment: toPayment(p.payment) }
}

export function toSuggestion(s: components['schemas']['RecurringSuggestionResponse']): Suggestion {
  return {
    id: s.id,
    merchant: s.merchant_name_display,
    frequency: normaliseFrequency(s.suggested_frequency),
    averageCents: parseMoney(s.suggested_average_amount),
    accountId: s.suggested_account_id ?? null,
    seenCount: s.matching_transaction_count,
  }
}
